"use client";
import { IconTile } from "@cmp-src/data-display/IconTile";
import { Button } from "@cmp-src/primitives/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@cmp-src/primitives/tooltip";
import {
  CreateWorkplaceRequestParams,
  UpdateWorkplaceRequestParams
} from "@gettactic/api";
import { ConfirmModal } from "@gettactic/components/src/overlay/modals/ConfirmModal";
import { cn } from "@gettactic/helpers";
import { mapWorkplaceRequestToReserveFormData } from "@gettactic/helpers/src/reserve/mapWorkplaceRequestToReserveFormData";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil, Plus, Trash2, Undo } from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useSubmittableWorkplaceRequestTypes } from "@/features/workplace-requests/hooks/useSubmittableWorkplaceRequestTypes";
import { useWorkplaceRequestMutations } from "@/features/workplace-requests/hooks/useWorkplaceRequestMutations";
import { useWorkplaceRequestsByReservation } from "@/features/workplace-requests/hooks/useWorkplaceRequestsByReservation";
import { useWorkplaceRequestTypeVisuals } from "@/features/workplace-requests/hooks/useWorkplaceRequestTypeVisuals";
import {
  WorkplaceRequestsFormValues,
  workplaceRequestsFormSchema
} from "@/features/workplace-requests/validation/workplace-requests";
import {
  CollectedWorkplaceRequestDraft,
  NewRequestWizard
} from "@/features/workplace-requests/views/queue/NewRequestWizard";
import { WorkplaceRequestParams } from "@/types/workplace-requests";
import { WPRequestsProps } from "./types";
import { WPRequestErrors } from "./WPRequestErrors";

type RequestsFormValues = WorkplaceRequestsFormValues;
type RequestItem = RequestsFormValues["requests"][number];

type WizardState =
  | { mode: "add" }
  | { mode: "edit"; index: number; draft: CollectedWorkplaceRequestDraft };

export const WPRequests: React.FC<WPRequestsProps> = ({
  reservationId,
  officeId,
  className,
  onSubmitRef,
  workplaceRequestItems,
  resource,
  reservationWhen
}) => {
  const [errors, setErrors] = useState<string[]>([]);
  const [nextId, setNextId] = useState(1);
  const [wizardState, setWizardState] = useState<WizardState | null>(null);
  // Remount the wizard per open so its initial step/type/hydration start fresh.
  const [wizardKey, setWizardKey] = useState(0);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  // Unfiltered on purpose: this list resolves the type of requests already attached to
  // the reservation, and a filtered one would render a restricted type as "Unknown
  // Type". The wizard opened from here does its own filtered fetch.
  const { allElements: types } = useSubmittableWorkplaceRequestTypes();
  const {
    updateMultipleWorkplaceRequests,
    deleteWorkplaceRequest,
    createMultipleWorkplaceRequests
  } = useWorkplaceRequestMutations();

  const form = useForm<RequestsFormValues>({
    resolver: zodResolver(workplaceRequestsFormSchema),
    defaultValues: {
      requests: workplaceRequestItems || []
    }
  });

  const { fields, remove, update } = useFieldArray({
    control: form.control,
    name: "requests"
  });

  const { allElements: existingRequests, isFetching: isLoadingExisting } =
    useWorkplaceRequestsByReservation(reservationId, {
      enabled: !!reservationId
    });

  const typeVisuals = useWorkplaceRequestTypeVisuals();

  useEffect(() => {
    if (existingRequests.length > 0 && fields.length === 0) {
      const mappedRequests = mapWorkplaceRequestToReserveFormData(
        existingRequests,
        types
      );

      // Update form with existing requests
      form.setValue("requests", mappedRequests as never);
    }
  }, [existingRequests, types, fields.length, form]);

  const openAddWizard = useCallback(() => {
    setWizardState({ mode: "add" });
    setWizardKey((k) => k + 1);
  }, []);

  const openEditWizard = useCallback(
    (index: number) => {
      const item = form.getValues("requests")[index];
      if (!item) return;
      const draft: CollectedWorkplaceRequestDraft = {
        draftId: item.id ?? `idx-${index}`,
        type: item.type,
        title: item.title,
        description: item.description || undefined,
        office_id: item.office_id || officeId,
        location: item.location,
        field_values: item.field_values ?? [],
        values: item.values ?? {
          title: item.title,
          description: item.description ?? "",
          office_id: item.office_id || officeId
        }
      };
      setWizardState({ mode: "edit", index, draft });
      setWizardKey((k) => k + 1);
    },
    [form, officeId]
  );

  const handleCollect = useCallback(
    (draft: CollectedWorkplaceRequestDraft) => {
      if (wizardState?.mode === "edit") {
        const index = wizardState.index;
        const current = form.getValues("requests")[index];
        update(index, {
          ...current,
          type: draft.type,
          title: draft.title,
          description: draft.description ?? "",
          office_id: draft.office_id,
          location: draft.location,
          field_values: draft.field_values,
          values: draft.values
        });
      } else {
        const newRequest: RequestItem = {
          id: `new-${nextId}`,
          type: draft.type,
          title: draft.title,
          description: draft.description ?? "",
          office_id: draft.office_id,
          location: draft.location,
          field_values: draft.field_values,
          values: draft.values,
          isNew: true,
          isDeleted: false
        };
        const currentRequests = form.getValues("requests");
        // Prepend so the newest request shows at the top.
        form.setValue("requests", [newRequest, ...currentRequests]);
        setNextId((prev) => prev + 1);
      }
      setWizardState(null);
    },
    [wizardState, form, update, nextId]
  );

  const handleConfirmDelete = useCallback(() => {
    if (deletingIndex === null) return;
    const current = form.getValues("requests");
    const request = current[deletingIndex];
    // Saved requests are kept and flagged so the reservation save deletes them;
    // not-yet-saved drafts are removed outright.
    if (request && !request.isNew && request.id) {
      update(deletingIndex, { ...request, isDeleted: true });
    } else {
      remove(deletingIndex);
    }
    setDeletingIndex(null);
  }, [deletingIndex, form, update, remove]);

  const handleUndoDelete = useCallback(
    (index: number) => {
      const request = form.getValues("requests")[index];
      if (!request) return;
      update(index, { ...request, isDeleted: false });
    },
    [form, update]
  );

  const validateAndGetRequests = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      return null;
    }

    return fields;
  }, [form, fields]);

  const submitRequests = useCallback(
    async (
      params: WorkplaceRequestParams
    ): Promise<{
      success: boolean;
      error?: string;
    }> => {
      const { scheduleId, ownerUserId } = params;
      try {
        setErrors([]);

        const requests =
          form
            .getValues("requests")
            .filter((req) => req.title || req.description) || [];

        if (!requests || requests.length === 0) {
          return { success: true };
        }

        // Categorize requests in a single pass
        const { newRequests, existingRequests } = requests.reduce(
          (acc, req) => {
            if (req.isNew) {
              acc.newRequests.push(req);
            } else if (!req.isDeleted && req.id) {
              acc.existingRequests.push(req);
            }
            return acc;
          },
          {
            newRequests: [] as typeof requests,
            existingRequests: [] as typeof requests
          }
        );

        let hasError = false;
        let errorMessage = "";

        if (newRequests.length > 0) {
          if (!scheduleId) {
            hasError = true;
            errorMessage = "No schedule ID provided for workplace requests";
            setErrors((prev) => [...prev, errorMessage]);
          } else {
            try {
              const createData = newRequests.map((req) => {
                const requestData: CreateWorkplaceRequestParams & {
                  submitter_id?: string;
                } = {
                  title: req.title,
                  type_id: req.type.id,
                  office_id: req.office_id || officeId,
                  schedule_id: scheduleId,
                  status: "open",
                  field_values: (req.field_values ?? []).map((fv) => ({
                    field_id: fv.field_id,
                    value: fv.value
                  }))
                };

                if (req.description?.trim()) {
                  requestData.description = req.description.trim();
                }
                if (req.location) {
                  requestData.location = req.location;
                }
                if (ownerUserId) {
                  requestData.submitter_id = ownerUserId;
                }
                return requestData;
              });

              await createMultipleWorkplaceRequests(createData);

              form.setValue("requests", []);

              setNextId(1);
            } catch (error) {
              hasError = true;
              errorMessage = "Failed to create new workplace requests";
              setErrors((prev) => [...prev, errorMessage]);
            }
          }
        }

        if (existingRequests.length > 0) {
          const updateData = existingRequests.map((req) => {
            const updates: UpdateWorkplaceRequestParams = {
              title: req.title,
              type_id: req.type.id,
              field_values: (req.field_values ?? []).map((fv) => ({
                field_id: fv.field_id,
                value: fv.value
              }))
            };

            if (req.description?.trim()) {
              updates.description = req.description.trim();
            }
            if (req.office_id) {
              updates.office_id = req.office_id;
            }
            if (req.location) {
              updates.location = req.location;
            }

            return {
              requestId: req.id!,
              updates
            };
          });

          try {
            await updateMultipleWorkplaceRequests(updateData);
          } catch (error) {
            hasError = true;
            errorMessage = "Failed to update existing requests";
            setErrors((prev) => [...prev, errorMessage]);
          }
        }

        const requestsToDelete = requests.filter(
          (req) => req.isDeleted && !req.isNew && req.id
        );
        if (requestsToDelete.length > 0) {
          try {
            await Promise.all(
              requestsToDelete.map((request) =>
                deleteWorkplaceRequest(request.id)
              )
            );

            const updatedRequests = requests.filter((req) => !req.isDeleted);
            form.setValue("requests", updatedRequests);
          } catch (error) {
            hasError = true;
            errorMessage = "Failed to delete some workplace requests";
            setErrors((prev) => [...prev, errorMessage]);
          }
        }

        if (hasError) {
          return { success: false, error: errorMessage };
        }

        return { success: true };
      } catch (error) {
        const errorMessage = "Failed to process workplace requests";
        setErrors((prev) => [...prev, errorMessage]);
        return { success: false, error: errorMessage };
      }
    },
    [
      form,
      officeId,
      createMultipleWorkplaceRequests,
      updateMultipleWorkplaceRequests,
      deleteWorkplaceRequest
    ]
  );

  useImperativeHandle(
    onSubmitRef,
    () => ({
      submitWorkplaceRequests: submitRequests,
      validateAndGetRequests
    }),
    [submitRequests, validateAndGetRequests]
  );

  if (isLoadingExisting) {
    return (
      <div className={cn("space-y-4", className)}>
        <div className="text-neutral-500 text-sm">
          Loading existing requests...
        </div>
      </div>
    );
  }

  return (
    <div className={cn("min-h-10 space-y-4", className)}>
      <div className="flex items-center justify-between gap-1">
        <span className="font-inter font-normal text-sm">
          Add Workplace Request(s)
        </span>
        <Button
          onClick={openAddWizard}
          variant="outline"
          size="sm"
          className="min-w-[120px]"
          type="button"
        >
          <Plus className="mr-1 h-4 w-4" />
          Add request
        </Button>
      </div>

      <WPRequestErrors errors={errors} />

      {fields.length > 0 ? (
        <div className="space-y-2">
          {fields.map((field, index) => {
            const isRemoved = !field.isNew && field.isDeleted;
            return (
              <div
                key={field.id}
                className={cn(
                  "flex items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-white px-3 py-2 shadow-sm",
                  isRemoved && "opacity-50"
                )}
              >
                <div className="flex min-w-0 items-center gap-3">
                  <IconTile
                    size="md"
                    color={typeVisuals.get(field.type.id)?.color}
                    icon={typeVisuals.get(field.type.id)?.icon}
                  />
                  <div className="min-w-0">
                    <p className="truncate font-medium text-neutral-900 text-sm">
                      {field.title || "Untitled request"}
                    </p>
                    <p className="truncate text-neutral-500 text-xs">
                      {field.type.name}
                      {isRemoved ? " · will be removed" : ""}
                    </p>
                  </div>
                </div>

                <div className="flex shrink-0 items-center gap-1">
                  {isRemoved ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => handleUndoDelete(index)}
                          className="h-7 w-7 p-0"
                        >
                          <Undo className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Undo removal</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => openEditWizard(index)}
                            className="h-7 w-7 p-0 text-neutral-500 hover:text-neutral-800"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Edit request</p>
                        </TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            onClick={() => setDeletingIndex(index)}
                            className="h-7 w-7 p-0 text-neutral-400 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Delete request</p>
                        </TooltipContent>
                      </Tooltip>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}

      {wizardState ? (
        <NewRequestWizard
          key={wizardKey}
          isOpen={true}
          onOpenChange={(open) => {
            if (!open) setWizardState(null);
          }}
          onCollect={handleCollect}
          initialDraft={
            wizardState.mode === "edit" ? wizardState.draft : undefined
          }
          // Pre-fill the location from the reservation's resource when adding a
          // new request; edits restore their own location from the draft.
          resource={wizardState.mode === "add" ? resource : undefined}
          reservationWhen={
            wizardState.mode === "add" ? reservationWhen : undefined
          }
          lockedOfficeId={officeId}
        />
      ) : null}

      <ConfirmModal
        isOpen={deletingIndex !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingIndex(null);
        }}
        title="Delete request"
        message="Are you sure you want to delete this request?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeletingIndex(null)}
      />
    </div>
  );
};
